import { create } from 'zustand';
import { messageService } from '../services/messageService';
import type { Message } from '../services/messageService';

interface MessageState {
  unreadCount: number;
  messages: Message[];
  loading: boolean;
  pollTimer: ReturnType<typeof setInterval> | null;

  loadUnreadCount: (username: string) => Promise<void>;
  loadMessages: (username: string, options?: { status?: 'all' | 'unread'; role?: string }) => Promise<void>;
  markAsRead: (messageId: number, username: string) => Promise<void>;
  startPolling: (username: string) => void;
  stopPolling: () => void;
}

export const useMessageStore = create<MessageState>((set, get) => ({
  unreadCount: 0,
  messages: [],
  loading: false,
  pollTimer: null,

  loadUnreadCount: async (username: string) => {
    try {
      const count = await messageService.getUnreadCount(username);
      set({ unreadCount: count });
    } catch {
      // silent
    }
  },

  loadMessages: async (username: string, options) => {
    set({ loading: true });
    try {
      const msgs = await messageService.getMessages(username, options);
      set({ messages: msgs, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  markAsRead: async (messageId: number, username: string) => {
    try {
      await messageService.markAsRead(messageId, username);
      set((s) => ({
        messages: s.messages.map((m) => m.id === messageId ? { ...m, read: true } : m),
        unreadCount: Math.max(0, s.unreadCount - 1),
      }));
    } catch {
      // silent
    }
  },

  startPolling: (username: string) => {
    const { pollTimer } = get();
    if (pollTimer) return;
    // Initial load
    get().loadUnreadCount(username);
    const timer = setInterval(() => get().loadUnreadCount(username), 60000);
    set({ pollTimer: timer });
  },

  stopPolling: () => {
    const { pollTimer } = get();
    if (pollTimer) {
      clearInterval(pollTimer);
      set({ pollTimer: null });
    }
  },
}));
